import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, FileText, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";
import { LangSwitcher } from "@/components/LangSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";

export const Header = () => {
  const { t } = useTranslation("header");
  const { t: tc } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { to: "/", label: t("nav.home"), end: true },
    { to: "/services", label: t("nav.services") },
    { to: "/packs", label: t("nav.packs") },
    { to: "/micro-services", label: t("nav.microservices") },
    { to: "/blog", label: t("nav.blog") },
  ];

  return (
    <header
      className={`safe-top sticky top-0 z-40 transition-all duration-500 ${
        scrolled
          ? "border-b border-primary/15 glass-strong shadow-glass"
          : "border-b border-transparent bg-background/30 backdrop-blur-md"
      }`}
    >
      {/* Halo aurora décoratif */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, hsl(var(--primary) / 0.45), hsl(var(--aurora-violet) / 0.45), hsl(var(--aurora-cyan) / 0.45), transparent)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 left-1/3 h-40 w-[40rem] rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(closest-side, hsl(var(--aurora-violet) / 0.55), transparent 70%)" }}
      />

      <div className="container relative flex h-14 items-center justify-between gap-3 sm:h-16 sm:gap-4">
        <Link to="/" aria-label="Conceptions.ma — Powered by Adhorizon" className="group relative inline-flex items-center">
          <span
            aria-hidden
            className="absolute -inset-2 rounded-full opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-100"
            style={{ background: "radial-gradient(closest-side, hsl(var(--primary-glow) / 0.55), transparent 70%)" }}
          />
          <span className="relative">
            <BrandLogo />
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `relative rounded-full px-4 py-2 text-sm font-medium transition-all duration-300 ${
                  isActive
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span
                      aria-hidden
                      className="absolute inset-0 -z-10 rounded-full border border-primary/30 bg-primary/10 backdrop-blur-sm"
                      style={{ boxShadow: "inset 0 1px 0 hsl(255 100% 100% / 0.06)" }}
                    />
                  )}
                  {!isActive && (
                    <span
                      aria-hidden
                      className="absolute inset-0 -z-10 rounded-full bg-foreground/0 transition-colors duration-300 hover:bg-foreground/5"
                    />
                  )}
                  {item.label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <ThemeToggle />
          <LangSwitcher />
          <Button
            asChild
            className="press-scale btn-aurora cta-shine relative h-12 gap-2 rounded-full border-0 px-8 text-base font-bold text-primary-foreground shadow-warm-lg"
          >
            <Link to="/devis">
              <Sparkles className="size-4" />
              {tc("cta_quote")}
            </Link>
          </Button>
        </div>

        <button
          type="button"
          className="press-scale relative inline-flex size-11 items-center justify-center rounded-full border border-primary/25 bg-card/40 text-foreground backdrop-blur lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? t("close_menu") : t("open_menu")}
          aria-expanded={open}
        >
          {open ? <X className="size-5" /> : <Menu className="size-5" />}
        </button>
      </div>

      {open && (
        <div className="glass-strong border-t border-primary/15 lg:hidden">
          <div className="container relative flex flex-col gap-1 py-4">
            <div
              aria-hidden
              className="pointer-events-none absolute -top-20 right-0 h-40 w-40 rounded-full opacity-50 blur-3xl"
              style={{ background: "radial-gradient(closest-side, hsl(var(--aurora-cyan) / 0.5), transparent 70%)" }}
            />
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `relative rounded-xl px-4 py-3 text-base font-medium transition-colors ${
                    isActive
                      ? "border border-primary/25 bg-primary/10 text-foreground"
                      : "border border-transparent text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="mt-2 flex items-center justify-between gap-3 border-t border-border/60 pt-4">
              <LangSwitcher />
              <ThemeToggle />
              <Button asChild className="btn-aurora cta-shine flex-1 gap-2 rounded-full border-0">
                <Link to="/devis" onClick={() => setOpen(false)}>
                  <FileText className="size-4" />
                  {tc("cta_quote")}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
